import { Header, Label } from '@mairistem/composants';
import { ListeTable } from '@mairistem/liste';
import dayjs from 'dayjs';
import _ from 'lodash';
import React from 'react';
import type { CycleType, LogicielFacturier } from '@/types';
import {
  formatMonetaire,
  getDateLimiteFinaleCycle,
  getStatutRelanceCycle,
  getStatutTransfertCycle,
} from '@/utils';
import CycleEditionImpayesPdf from './CycleEditionImpayesPdf';

interface CyclesListeTableRowImpayesProps {
  item: CycleType;
  regieProlongee?: boolean;
  joursEncaissementSupplementaires?: number;
  logicielsFacturiers?: LogicielFacturier[];
  rowProps: Record<string, unknown>;
  invalidate?: () => void;
}

const CyclesListeTableRowImpayes = (props: CyclesListeTableRowImpayesProps) => {
  const {
    item,
    regieProlongee,
    joursEncaissementSupplementaires,
    logicielsFacturiers,
    rowProps,
    invalidate,
  } = props;

  // Si 1 seul logiciel facturier, on masque la colonne
  const hideSourceColumn = _.size(logicielsFacturiers) <= 1;

  const {
    identifiant,
    titre,
    dateIncorporation,
    dateEmission,
    dateLimite,
    dateValidationPosteComptable,
    logicielFacturier: { code },
    nombreFactures,
    nombreFacturesImpayes,
    montant,
    montantEncaisse,
    relances,
    dateTransfertImpaye,
    dateTransfertTitre,
    identifiantDocumentImpayesPdf,
  } = item;

  const cyleDateEmission = `${titre}${dateEmission ? ` - ${dayjs(dateEmission).format('DD/MM/YYYY')}` : ''}`;

  const dateLimiteFinale = getDateLimiteFinaleCycle(
    dateLimite,
    regieProlongee,
    joursEncaissementSupplementaires,
  );
  const statutRelance = getStatutRelanceCycle(relances, dateLimiteFinale);
  const statutTransfert = getStatutTransfertCycle(
    { dateTransfertImpaye, dateTransfertTitre, dateValidationPosteComptable },
    regieProlongee,
    dateLimiteFinale,
  );

  React.useEffect(() => {
    invalidate?.();
  }, []);

  return (
    <ListeTable.Row
      key={identifiant}
      {...rowProps}
      // {...identifiant === 2 && { disabled: true }}
    >
      <ListeTable.Cell
        name="dateIncorporation"
        textAlign="center"
        // @ts-expect-error
        title={dateIncorporation}
        style={{ width: 150, height: 80 }}
        verticalAlign="middle"
      >
        {dateIncorporation ? dayjs(dateIncorporation).format('DD/MM/YYYY') : '-'}
      </ListeTable.Cell>
      <ListeTable.Cell
        name="titre"
        // @ts-expect-error
        title={cyleDateEmission}
        verticalAlign="middle"
        style={{ position: 'relative' }}
      >
        <div style={{ display: 'flex' }}>
          <div style={{ flex: 1 }}>
            {titre}
            {!_.isNil(dateEmission) && (
              <span className="hint">
                {!_.isEmpty(dateEmission) ? dayjs(dateEmission).format('DD/MM/YYYY') : ''}
              </span>
            )}
          </div>
        </div>
      </ListeTable.Cell>
      {!hideSourceColumn && (
        <ListeTable.Cell
          name="logicielFacturier"
          textAlign="center"
          // @ts-expect-error
          title={code}
          style={{ width: 90 }}
          verticalAlign="middle"
        >
          {code}
        </ListeTable.Cell>
      )}
      <ListeTable.Cell
        name="dateLimite"
        textAlign="center"
        // @ts-expect-error
        title={dateLimiteFinale}
        style={{ width: 150 }}
        verticalAlign="middle"
      >
        {
          regieProlongee ? (
            dayjs(dateLimite).isAfter(dayjs(), 'day') ? ( // date limite de base pas encore atteinte donc RAS
              dateLimiteFinale ? (
                dayjs(dateLimiteFinale).format('DD/MM/YYYY')
              ) : (
                '-'
              ) // noir
            ) : dayjs(dateLimiteFinale).isSameOrAfter(dayjs(), 'day') ? ( // si entre la date limite de base et la date limite prolongée
              <Header as="h5" color="orange" style={{ fontWeight: 600 }}>
                {dayjs(dateLimiteFinale).format('DD/MM/YYYY')}
              </Header>
            ) : (
              <Header as="h5" color="red" style={{ fontWeight: 600 }}>
                {dayjs(dateLimiteFinale).format('DD/MM/YYYY')}
              </Header>
            ) // date limite prolongée dépassée
          ) : dateLimiteFinale ? (
            dayjs(dateLimiteFinale).format('DD/MM/YYYY')
          ) : (
            '-'
          ) /* Si pas en régie prolongée, noir */
        }
      </ListeTable.Cell>
      <ListeTable.Cell
        name="montant"
        textAlign="right"
        // @ts-expect-error
        title={formatMonetaire(montant)}
        style={{ width: 110 }}
        verticalAlign="middle"
      >
        {formatMonetaire(montant)}
      </ListeTable.Cell>
      <ListeTable.Cell
        name="resteEncaisse"
        textAlign="right"
        // @ts-expect-error
        title={formatMonetaire(montant - montantEncaisse)}
        style={{ width: 110 }}
        verticalAlign="middle"
      >
        {formatMonetaire(montant - montantEncaisse)}
      </ListeTable.Cell>
      <ListeTable.Cell
        name="nombreFacturesImpayes"
        // @ts-expect-error
        title={nombreFactures}
        style={{ width: 100 }}
        verticalAlign="middle"
      >
        {nombreFacturesImpayes}
      </ListeTable.Cell>
      {regieProlongee && (
        <ListeTable.Cell
          name="relances"
          // @ts-expect-error
          title={_.isEmpty(relances) ? '' : dayjs(_.last(relances).date).format('DD/MM/YYYY')}
          style={{ width: 150, position: 'relative' }}
          textAlign="center"
          verticalAlign="middle"
        >
          <div style={{ display: 'flex' }}>
            <div style={{ flex: 1 }}>
              <Label
                size="small"
                circular
                {...(statutRelance !== 'Non réalisée' && {
                  color: statutRelance === 'A faire' ? 'orange' : 'green',
                })}
                style={{
                  width: '100%',
                  ...(statutRelance === 'Non réalisée' && {
                    opacity: 0.75,
                    fontStyle: 'italic',
                  }),
                }}
                {...(statutRelance === 'Non réalisée' && {
                  basic: true,
                })}
              >
                <Header
                  as="h5"
                  style={{ margin: 0 }}
                  color={
                    statutRelance === 'Non réalisée'
                      ? 'grey'
                      : statutRelance === 'A faire'
                        ? 'orange'
                        : 'green'
                  }
                >
                  {statutRelance}
                </Header>
              </Label>
            </div>
            {!_.isEmpty(relances) && (
              <span className="hint centered">
                {dayjs(_.last(relances).date).format('DD/MM/YYYY')}
              </span>
            )}
          </div>
        </ListeTable.Cell>
      )}
      <ListeTable.Cell name="etat" style={{ width: 125 }} verticalAlign="middle">
        {statutTransfert && (
          <Label
            size="small"
            circular
            color={statutTransfert.couleur}
            style={{ width: '100%' }}
            title={statutTransfert.tooltip}
          >
            <Header as="h5" style={{ margin: 0 }} color={statutTransfert.couleur}>
              {statutTransfert.statut}
            </Header>
          </Label>
        )}
      </ListeTable.Cell>
      <ListeTable.Cell
        name="edition"
        style={{ width: 100 }}
        textAlign="center"
        verticalAlign="middle"
      >
        <CycleEditionImpayesPdf identifiantDocument={identifiantDocumentImpayesPdf} />
      </ListeTable.Cell>
    </ListeTable.Row>
  );
};

export default CyclesListeTableRowImpayes;
